import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import type { AuditLogViewerComponent } from '../components/audit-log-viewer/audit-log-viewer.component';
import type {
  AuditLogViewerConfig,
  DocumentIdentifiers,
} from '../components/audit-log-viewer/audit-log-viewer.types';
import { MODAL_CONFIG } from '../components/audit-log-viewer/audit-log-viewer.constants';

/**
 * Audit Log Viewer Service
 *
 * Global service to open the audit log viewer modal from anywhere in the application.
 * Provides convenience methods for common use cases.
 *
 * The viewer is loaded on demand with a dynamic import, so it stays out of the initial
 * bundle. Every method returns a promise that resolves once the chunk has loaded and the
 * dialog is open. Callers that only need to show the modal can ignore the returned value.
 *
 * @example
 * ```typescript
 * // Basic usage
 * private auditLogViewerService = inject(AuditLogViewerService);
 *
 * openLogs(): void {
 *   this.auditLogViewerService.open({
 *     documentIdentifiers: { _id: '123abc', id: 456 },
 *     entityName: 'Business Partner XYZ',
 *     collectionName: 'business-partner',
 *   });
 * }
 *
 * // Awaiting the dialog reference
 * async openAndWait(): Promise<void> {
 *   const dialogRef = await this.auditLogViewerService.openByMongoId('675e16f14ae99c00124ab001');
 *   await firstValueFrom(dialogRef.afterClosed());
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AuditLogViewerService {
  private dialog = inject(MatDialog);

  /**
   * Open the audit log viewer modal with full configuration
   *
   * @param config - Configuration for the modal
   * @returns Promise resolving to the MatDialogRef for the opened modal
   */
  async open(config: AuditLogViewerConfig): Promise<MatDialogRef<AuditLogViewerComponent>> {
    const { AuditLogViewerComponent } = await import(
      '../components/audit-log-viewer/audit-log-viewer.component'
    );

    return this.dialog.open(AuditLogViewerComponent, {
      width: MODAL_CONFIG.width,
      maxWidth: MODAL_CONFIG.maxWidth,
      maxHeight: MODAL_CONFIG.maxHeight,
      disableClose: false,
      panelClass: MODAL_CONFIG.panelClass,
      data: config,
    });
  }

  /**
   * Convenience method to open by MongoDB ObjectId
   *
   * @param mongoId - MongoDB ObjectId as string
   * @param entityName - Optional name to display in title
   * @param collectionName - Optional collection name filter
   * @param useMockData - Use mock data (for development)
   * @returns Promise resolving to the MatDialogRef for the opened modal
   */
  openByMongoId(
    mongoId: string,
    entityName?: string,
    collectionName?: string,
    useMockData = false
  ): Promise<MatDialogRef<AuditLogViewerComponent>> {
    return this.open({
      documentIdentifiers: { _id: mongoId },
      entityName,
      collectionName,
      useMockData,
    });
  }

  /**
   * Convenience method to open by numeric ID
   *
   * @param id - Numeric ID
   * @param entityName - Optional name to display in title
   * @param collectionName - Optional collection name filter
   * @param useMockData - Use mock data (for development)
   * @returns Promise resolving to the MatDialogRef for the opened modal
   */
  openById(
    id: number,
    entityName?: string,
    collectionName?: string,
    useMockData = false
  ): Promise<MatDialogRef<AuditLogViewerComponent>> {
    return this.open({
      documentIdentifiers: { id },
      entityName,
      collectionName,
      useMockData,
    });
  }

  /**
   * Convenience method to open by code
   *
   * @param code - External code identifier
   * @param entityName - Optional name to display in title
   * @param collectionName - Optional collection name filter
   * @param useMockData - Use mock data (for development)
   * @returns Promise resolving to the MatDialogRef for the opened modal
   */
  openByCode(
    code: string,
    entityName?: string,
    collectionName?: string,
    useMockData = false
  ): Promise<MatDialogRef<AuditLogViewerComponent>> {
    return this.open({
      documentIdentifiers: { code },
      entityName,
      collectionName,
      useMockData,
    });
  }

  /**
   * Convenience method to open with multiple identifiers
   *
   * @param identifiers - Document identifiers object
   * @param entityName - Optional name to display in title
   * @param collectionName - Optional collection name filter
   * @param useMockData - Use mock data (for development)
   * @returns Promise resolving to the MatDialogRef for the opened modal
   */
  openWithIdentifiers(
    identifiers: DocumentIdentifiers,
    entityName?: string,
    collectionName?: string,
    useMockData = false
  ): Promise<MatDialogRef<AuditLogViewerComponent>> {
    return this.open({
      documentIdentifiers: identifiers,
      entityName,
      collectionName,
      useMockData,
    });
  }
}
